import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { Alert, Button, Modal, Spin, Table, Tag, Typography } from 'antd'

import { camelize } from '../../../../helpers/string'

// Translate a document type/flow slug, falling back to the raw slug for
// values the locale files don't know yet.
const slugLabel = (i18n, group, slug) => {
  const key = `confirms:accounting.syncAccount.${group}.${camelize(slug)}`

  return i18n.exists(key) ? i18n.t(key) : slug
}

// present: true = exists on osh-account (skip), false = will be sent,
// null = the existence check itself failed.
const statusTag = (i18n, document) => {
  if (document.present === true) {
    return <Tag color="green">{i18n.t('confirms:accounting.syncAccount.status.present')}</Tag>
  }
  if (document.present === false) {
    return <Tag color="orange">{i18n.t('confirms:accounting.syncAccount.status.missing')}</Tag>
  }

  return <Tag color="red">{i18n.t('confirms:accounting.syncAccount.status.checkFailed')}</Tag>
}

const documentKey = (document) => `${document.type}-${document.code}`

const SyncAccountButtonRender = ({
  modalOpen,
  onOpen,
  onClose,
  onRetry,
  pickedKeys,
  onPick,
  onConfirm,
  checking,
  syncing,
  checkFailed,
  documents
}) => {
  const { i18n } = useTranslation()

  const missingCount = documents.filter((document) => document.present === false).length
  const presentCount = documents.filter((document) => document.present === true).length
  const errorCount = documents.length - missingCount - presentCount
  // Unverified rows stay sendable: the sync job re-derives the missing set
  // itself, so sending despite a failed check is safe.
  const sendable = documents.filter((document) => document.present !== true)
  const hasSendable = sendable.length > 0
  const selectedRowKeys = pickedKeys || sendable.map(documentKey)
  const picked = sendable.filter((document) => selectedRowKeys.includes(documentKey(document)))
  const canConfirm = picked.length > 0

  const handleConfirm = () => {
    if (picked.length === sendable.length) return onConfirm(null)

    onConfirm(picked.map(({ type, code }) => ({ type, code })))
  }

  const columns = [
    {
      title: i18n.t('confirms:accounting.syncAccount.columns.document'),
      key: 'document',
      render: (_, document) => (
        <React.Fragment>
          <Tag>{slugLabel(i18n, 'flows', document.flow)}</Tag>
          {slugLabel(i18n, 'documentTypes', document.type)}
        </React.Fragment>
      )
    },
    {
      title: i18n.t('confirms:accounting.syncAccount.columns.code'),
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Typography.Text code>{code}</Typography.Text>
    },
    {
      title: i18n.t('confirms:accounting.syncAccount.columns.status'),
      key: 'status',
      render: (_, document) => statusTag(i18n, document)
    }
  ]

  const footer = [
    <Button key="close" onClick={onClose}>
      {i18n.t('confirms:accounting.syncAccount.close')}
    </Button>
  ]
  if (!checking && !checkFailed && canConfirm) {
    footer.push(
      <Button key="confirm" type="primary" loading={syncing} onClick={handleConfirm}>
        {i18n.t('confirms:accounting.syncAccount.confirm', { selected: picked.length })}
      </Button>
    )
  }

  const renderBody = () => {
    if (checking) {
      return (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Spin />
        </div>
      )
    }

    if (checkFailed) {
      return (
        <Alert
          type="error"
          showIcon
          message={i18n.t('confirms:accounting.syncAccount.checkFailedTitle')}
          description={i18n.t('confirms:accounting.syncAccount.checkFailedDescription')}
          action={
            <Button size="small" onClick={onRetry}>
              {i18n.t('confirms:accounting.syncAccount.retry')}
            </Button>
          }
        />
      )
    }

    if (documents.length === 0) {
      return (
        <Alert
          type="info"
          showIcon
          message={i18n.t('confirms:accounting.syncAccount.emptyTitle')}
          description={i18n.t('confirms:accounting.syncAccount.emptyDescription')}
        />
      )
    }

    const summary = hasSendable
      ? i18n.t('confirms:accounting.syncAccount.summary', {
        missing: missingCount,
        present: presentCount
      })
      : i18n.t('confirms:accounting.syncAccount.summaryAllPresent', { total: documents.length })

    return (
      <React.Fragment>
        <Typography.Paragraph>{summary}</Typography.Paragraph>

        {errorCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
            message={i18n.t('confirms:accounting.syncAccount.partialFailure', {
              failed: errorCount
            })}
          />
        )}

        <Table
          rowKey={documentKey}
          rowSelection={{
            selectedRowKeys,
            onChange: onPick,
            getCheckboxProps: (document) => ({ disabled: document.present === true })
          }}
          columns={columns}
          dataSource={documents}
          pagination={false}
          size="small"
        />

        {hasSendable && (
          <Typography.Paragraph type="secondary" style={{ marginTop: '16px', marginBottom: 0 }}>
            {i18n.t('confirms:accounting.syncAccount.resendFailureNote')}
          </Typography.Paragraph>
        )}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Button onClick={onOpen}>
        {i18n.t('confirms:accounting.syncAccount.button')}
      </Button>

      <Modal
        title={i18n.t('confirms:accounting.syncAccount.title')}
        open={modalOpen}
        onCancel={onClose}
        footer={footer}
        width={640}>
        {renderBody()}
      </Modal>
    </React.Fragment>
  )
}

const documentShape = PropTypes.shape({
  type: PropTypes.string.isRequired,
  flow: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  present: PropTypes.bool,
  error: PropTypes.string
})

SyncAccountButtonRender.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  pickedKeys: PropTypes.arrayOf(PropTypes.string),
  onPick: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  checking: PropTypes.bool.isRequired,
  syncing: PropTypes.bool.isRequired,
  checkFailed: PropTypes.bool.isRequired,
  documents: PropTypes.arrayOf(documentShape).isRequired
}

export { SyncAccountButtonRender }
